import "dayjs/locale/uk";

import { DatePicker } from "antd";
import ukUA from "antd/es/date-picker/locale/uk_UA";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import updateLocale from "dayjs/plugin/updateLocale";
import { FC, useEffect, useState } from "react";
import { backendDateFormat } from "tm-shared/constants";
import { DateRange } from "tm-shared/types";

import {
    StyledRangeController,
    StyledRangeControllers,
    StyledRangeWrapper,
} from "./Styled";

dayjs.extend(localeData);
dayjs.extend(updateLocale);
dayjs.locale("uk");

interface ShipmentRangePickerProps {
    setDatesConfirmed: (value: DateRange) => void;
    limits: DateRange;
    datesConfirmed?: DateRange;
    disabled: boolean;
}

const { RangePicker } = DatePicker;

const checkMinMaxDate = (dates: [Dayjs, Dayjs]) => {
    const [date1, date2] = dates;
    const validDate1 = date1 || date2;
    const validDate2 = date2 || date1;

    return validDate1.isBefore(validDate2)
        ? [validDate1, validDate2]
        : [validDate2, validDate1];
};

export const ShipmentRangePicker: FC<ShipmentRangePickerProps> = ({
    setDatesConfirmed,
    limits,
    datesConfirmed,
    disabled,
}) => {
    const [dates, setDates] = useState<[Dayjs, Dayjs] | null>(null);
    const [isConfirmedDate, setIsConfirmedDate] = useState(false);
    const START_DATE = dayjs(limits.startDate);
    const END_DATE = dayjs(limits.endDate);
    const TODAY = dayjs();
    const DEFAULT_START = START_DATE.isSame(TODAY, "month")
        ? START_DATE
        : END_DATE.startOf("month").isBefore(START_DATE, "day")
          ? START_DATE
          : END_DATE.startOf("month");
    const DEFAULT_END = END_DATE;

    useEffect(() => {
        if (datesConfirmed) {
            setDates([
                dayjs(datesConfirmed.startDate),
                dayjs(datesConfirmed.endDate),
            ]);
            return;
        }

        const initialDates: [Dayjs, Dayjs] = [DEFAULT_START, DEFAULT_END];
        setDates(initialDates);
        setDatesConfirmed({
            startDate: DEFAULT_START.format(backendDateFormat.date),
            endDate: DEFAULT_END.format(backendDateFormat.date),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datesConfirmed?.startDate, datesConfirmed?.endDate]);

    useEffect(() => {
        if (isConfirmedDate && dates?.length && dates[0] && dates[1]) {
            const [minDate, maxDate] = checkMinMaxDate(dates);
            setDatesConfirmed({
                startDate: minDate.format(backendDateFormat.date),
                endDate: maxDate.format(backendDateFormat.date),
            });
            setIsConfirmedDate(false);
        }
    }, [dates, isConfirmedDate, setDatesConfirmed]);

    const disabledDate = (current: Dayjs) => {
        if (!dates || (dates[0] === undefined && dates[1] === undefined)) {
            return (
                current.isBefore(START_DATE, "day") ||
                current.isAfter(END_DATE, "day")
            );
        }

        const [minDate, maxDate] = checkMinMaxDate(dates);

        const minSelectableDate = minDate.subtract(2, "months");
        const maxSelectableDate = maxDate.add(2, "months");

        return (
            current.isBefore(minSelectableDate, "day") ||
            current.isAfter(maxSelectableDate, "day") ||
            current.isBefore(START_DATE, "day") ||
            current.isAfter(END_DATE, "day")
        );
    };

    const getRangeDates = (range: string): [Dayjs, Dayjs] => {
        const now = TODAY;

        if (range === "thisMonth") {
            if (
                START_DATE.isSame(now, "month") &&
                END_DATE.isSame(now, "month")
            ) {
                return [START_DATE, END_DATE];
            }

            if (START_DATE.isSame(now, "month")) {
                return [START_DATE, now];
            }

            if (END_DATE.isSame(now, "month")) {
                return [now.startOf("month"), END_DATE];
            }
            return [now.startOf("month"), now.endOf("month")];
        }

        if (range === "lastMonth") {
            const lastMonth = now.subtract(1, "month");

            if (START_DATE.isSame(lastMonth, "month")) {
                return [START_DATE, lastMonth.endOf("month")];
            }

            if (START_DATE.isSame(now, "month")) {
                return [START_DATE, now];
            }

            return [lastMonth.startOf("month"), lastMonth.endOf("month")];
        }

        return [DEFAULT_START, DEFAULT_END];
    };

    const isRangeDisabled = (range: string): boolean => {
        if (range === "lastMonth" && dates) {
            const lastMonth = TODAY.subtract(1, "month");
            if (START_DATE.isSame(TODAY, "month")) {
                return true;
            }

            return (
                lastMonth.endOf("month").isBefore(START_DATE, "day") ||
                lastMonth.startOf("month").isAfter(END_DATE, "day")
            );
        }

        const [start, end] = getRangeDates(range);
        return (
            start.isBefore(START_DATE, "day") || end.isAfter(END_DATE, "day")
        );
    };

    const handleDateChange = (newDates: [Dayjs, Dayjs] | null) => {
        setDates(newDates);
    };

    const handleRangeButtonClick = (range: string) => {
        const newDates = getRangeDates(range);
        setDates(newDates);
    };

    const handleOpenChange = (open: boolean) => {
        setIsConfirmedDate(!open);
    };

    const rangeButtons = [
        { label: "Минулий місяць", key: "lastMonth" },
        { label: "Поточний місяць", key: "thisMonth" },
    ];

    return (
        <StyledRangeWrapper>
            <RangePicker
                disabled={disabled}
                size="large"
                value={dates}
                onCalendarChange={setDates}
                onChange={handleDateChange}
                disabledDate={disabledDate}
                format="DD MMMM YYYY"
                allowClear
                onOpenChange={handleOpenChange}
                locale={ukUA}
                renderExtraFooter={() => (
                    <StyledRangeControllers>
                        {rangeButtons.map(({ label, key }) => (
                            <StyledRangeController
                                key={key}
                                type="primary"
                                onClick={() => handleRangeButtonClick(key)}
                                disabled={isRangeDisabled(key)}
                            >
                                {label}
                            </StyledRangeController>
                        ))}
                    </StyledRangeControllers>
                )}
            />
        </StyledRangeWrapper>
    );
};
